import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Header } from './components/Header';
import { PublicDashboard } from './components/public-dashboard';
import { Admin } from './pages/Admin';
import { AdminDashboard } from './pages/AdminDashboard';
import { AdminTesting } from './pages/AdminTesting';
import { NotFound } from './pages/NotFound';
import SettingsPage from './pages/email/settings-token';
import UnsubscribedPage from './pages/email/unsubscribed';
import VerifyEmailPage from './pages/email/verify-token';
import UnsubscribePage from './pages/email/unsubscribe-token';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import './App.css';
import './i18n';
import { SeoHead } from './components/SeoHead';
import i18n from './i18n';
import { useEffect } from 'react';
import { detectDefaultLocale, getLocaleFromPath, swapLocaleInPath } from './lib/urls';
import { useSeo } from './hooks/useSeo';

// Locale helpers centralized in lib/urls

function LocaleLayout({ children }: { children: React.ReactNode }) {
  const { locale = 'et' } = useParams();
  const location = useLocation();
  const rawLocale = (locale || 'et').toLowerCase();
  const loc = getLocaleFromPath(`/${rawLocale}/`);
  const needsRedirect = rawLocale !== loc;
  useEffect(() => {
    if (i18n.language !== loc) {
      i18n.changeLanguage(loc);
      document.documentElement.lang = loc;
    }
  }, [loc]);

  const seo = useSeo();

  if (needsRedirect) {
    const targetPath = swapLocaleInPath(location.pathname, loc);
    return <Navigate to={`${targetPath}${location.search}${location.hash}`} replace />;
  }
  return (
    <div className="App min-h-screen tc-bg-secondary">
      <SeoHead seo={seo} />
      <Header />
      <main>{children}</main>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <Routes>
          <Route path="/" element={<Navigate to={`/${detectDefaultLocale()}/`} replace />} />
          <Route path=":locale/" element={<LocaleLayout><PublicDashboard /></LocaleLayout>} />
          <Route path=":locale/terms" element={<LocaleLayout><Terms /></LocaleLayout>} />
          <Route path=":locale/privacy" element={<LocaleLayout><Privacy /></LocaleLayout>} />
          { /* Article index route disabled to avoid exposing parsed articles */ }
          {/* Admin and email flows remain non-localized paths */}
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/testing" element={<AdminTesting />} />
          <Route path="/settings/:token" element={<SettingsPage />} />
          <Route path="/verify/:token" element={<VerifyEmailPage />} />
          <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
          <Route path="/unsubscribed" element={<UnsubscribedPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
